import React from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Typography, Button, IconButton, TextField } from '@mui/material';
import _ from 'lodash';
import SnackbarUtils from 'src/common/utils/SnackbarUtilsConfigurator';

import { useUpdateScansMutation } from '../../../../common/apis/AdminApi';
import { ReactComponent as CancelIconBlue } from '../../../../common/assets/icons/cancel_icon_blue.svg';
import { User } from '../../../../common/types/User';

type Props = {
  user: User;
  onClose: () => void;
};

const EditScanCountPopup: React.FC<Props> = ({ onClose, user }) => {
  const [value, setValue] = React.useState('');
  const [updateScans, { isLoading }] = useUpdateScansMutation();

  const onKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Enter' && isValid() && !_.isEmpty(value)) {
      editScanCount();
    }
  };

  const editScanCount = async () => {
    updateScans({
      id: user.id.toString(),
      scans: value.toLowerCase() === 'unlimited' ? -1 : parseInt(value),
    })
      .unwrap()
      .then((res) => {
        SnackbarUtils.success(res.message);
        onClose();
      });
  };

  const isValid = () => {
    const regex = new RegExp('^(?:[0-9]*|unlimited)$');
    return regex.test(value);
  };

  return (
    <Box
      sx={{
        maxWidth: '456px',
        minHeight: '274px',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flex: 1,
        borderRadius: '15px',
        padding: '40px',
        flexDirection: 'column',
      }}
    >
      <Box className='row' sx={{ alignItems: 'center' }}>
        <IconButton
          onClick={onClose}
          disabled={isLoading}
          sx={{ alignSelf: 'flex-start', justifySelf: 'right' }}
        >
          <CancelIconBlue />
        </IconButton>
        <Typography
          sx={{
            alignSelf: 'center',
            whiteSpace: 'break-spaces',
            textAlign: 'center',
            mb: '24px',
            ml: 'auto',
            mr: 'auto',
          }}
          color='text.primary'
          variant='h5'
        >
          Edit Scan Count
        </Typography>
        <Box sx={{ mr: 'auto' }} />
      </Box>
      <TextField
        sx={{ mt: '30px' }}
        placeholder={
          user.purchasedScansAvailable === -1
            ? 'unlimited'
            : `${user.purchasedScansAvailable}`
        }
        onKeyUp={onKeyUp}
        variant='standard'
        fullWidth
        type='text'
        value={value}
        onChange={(e) => setValue(e.target.value)}
        inputProps={{
          maxLength: 9,
        }}
        helperText={
          !isValid()
            ? "Input should be equal or greater than 0 or 'unlimited'"
            : ''
        }
        error={!isValid()}
      />
      <Box
        sx={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'center',
          display: 'flex',
          marginTop: '20px',
        }}
      >
        <Button
          onClick={onClose}
          disabled={isLoading}
          disableElevation
          color='appGray2'
          variant='contained'
          sx={{
            borderRadius: '50px',
            width: '180px',
            height: '60px',
            marginRight: '15px',
          }}
        >
          <Typography color='white'>Cancel</Typography>
        </Button>
        <LoadingButton
          disabled={_.isEmpty(value) || !isValid()}
          onClick={editScanCount}
          loading={isLoading}
          disableElevation
          color='primary'
          variant='contained'
          sx={{
            borderRadius: '50px',
            width: '180px',
            height: '60px',
          }}
        >
          <Typography color='white'>Ok</Typography>
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default EditScanCountPopup;
